<template>
  <div class="case-import">
    <div class="page-header mb-3">
      <h4 class="mb-1">案例题批量导入</h4>
      <p class="text-muted mb-0">
        教师侧先用大模型把题库 docx 解析为 questions.json 与 images/ 目录（格式见
        docs/product-specs/case-study-questions.md 附录），再在此分两步入库
      </p>
    </div>

    <!-- 第一步：上传图片 -->
    <div class="card mb-3">
      <div class="card-body">
        <h6 class="card-title">第一步：上传图片目录 <span class="text-muted">（jpg/jpeg/png/gif/webp，单张 ≤5MB，可多选或选择整个文件夹）</span></h6>
        <div class="d-flex gap-2 mb-2 flex-wrap">
          <label class="btn btn-outline-primary">
            选择图片文件夹
            <input type="file" multiple webkitdirectory hidden @change="onPickImages($event)" />
          </label>
          <label class="btn btn-outline-primary">
            多选图片文件
            <input type="file" multiple accept="image/*" hidden @change="onPickImages($event)" />
          </label>
          <button
            class="btn btn-primary"
            :disabled="pendingFiles.length === 0 || uploading"
            @click="uploadAllImages"
          >
            {{ uploading ? `上传中（${uploadedCount}/${pendingFiles.length}）…` : `上传 ${pendingFiles.length} 张图片` }}
          </button>
        </div>
        <div v-if="uploadError" class="alert alert-danger py-2">{{ uploadError }}</div>
        <div v-if="imageMap && Object.keys(imageMap).length > 0" class="image-map-summary">
          <span class="text-success">
            <i class="bi bi-check-circle me-1"></i>已上传 {{ Object.keys(imageMap).length }} 张图片
          </span>
          <details class="mt-1">
            <summary class="text-muted small">查看映射</summary>
            <ul class="small mb-0">
              <li v-for="(url, filename) in imageMap" :key="filename">
                <code>{{ filename }}</code> → <code>{{ url }}</code>
              </li>
            </ul>
          </details>
        </div>
      </div>
    </div>

    <!-- 第二步：导入 JSON -->
    <div class="card mb-3">
      <div class="card-body">
        <h6 class="card-title">第二步：导入 questions.json</h6>
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label">题库 domain <span class="text-danger">*</span></label>
            <input v-model="meta.domain" type="text" class="form-control" placeholder="如：职业病诊断" />
          </div>
          <div class="col-md-4">
            <label class="form-label">题库 subdomain <span class="text-danger">*</span></label>
            <input v-model="meta.subdomain" type="text" class="form-control" placeholder="如：职业性噪声聋" />
          </div>
          <div class="col-md-4">
            <label class="form-label">题库代码 subDomainCode <span class="text-danger">*</span></label>
            <input v-model="meta.subDomainCode" type="text" class="form-control" placeholder="如：GBZ49" />
          </div>
          <div class="col-md-8">
            <label class="form-label">JSON 文件</label>
            <input type="file" accept=".json,application/json" class="form-control" @change="onPickJson" />
          </div>
          <div class="col-md-4 d-flex align-items-end">
            <div class="form-check">
              <input id="overwrite-check" v-model="overwrite" type="checkbox" class="form-check-input" />
              <label for="overwrite-check" class="form-check-label">覆盖同名题库（清空后重建）</label>
            </div>
          </div>
        </div>

        <div v-if="jsonError" class="alert alert-danger py-2 mt-3">{{ jsonError }}</div>
        <div v-if="jsonWarning" class="alert alert-warning py-2 mt-3">{{ jsonWarning }}</div>
        <div v-if="parsed" class="mt-3">
          <div class="alert alert-info py-2">
            解析结果：案例 <strong>{{ parsed.cases.length }}</strong> 个（子题
            {{ parsed.cases.reduce((sum, item) => sum + item.subQuestions.length, 0) }} 道）、普通题
            <strong>{{ parsed.quiz.length }}</strong> 道；图片引用
            {{ matchedRefCount }}/{{ totalImageRefCount }} 已匹配。
          </div>
          <button class="btn btn-success" :disabled="importing" @click="submitImport">
            {{ importing ? '导入中…' : '开始导入' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { questionBanksApi, type ImportQuestionBankRequest } from '@/api/questionBanks'
import { uploadApi } from '@/api/upload'

const router = useRouter()

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp)$/i
const BATCH_SIZE = 50

const pendingFiles = ref<File[]>([])
const uploading = ref(false)
const uploadedCount = ref(0)
const uploadError = ref('')
const imageMap = ref<Record<string, string>>({})

const meta = reactive({ domain: '', subdomain: '', subDomainCode: '' })
const overwrite = ref(false)
const jsonError = ref('')
const jsonWarning = ref('')
const importing = ref(false)
const bankTitle = ref('')

type ParsedCase = NonNullable<ImportQuestionBankRequest['questionBankData']['cases']>[number]
type ParsedQuizItem = NonNullable<ImportQuestionBankRequest['questionBankData']['quiz']>[number]

interface ParsedBank {
  title?: string
  cases: ParsedCase[]
  quiz: ParsedQuizItem[]
}

const parsed = ref<ParsedBank | null>(null)

const onPickImages = (event: Event) => {
  const input = event.target as HTMLInputElement
  const files = Array.from(input.files ?? []).filter(file => IMAGE_EXTENSIONS.test(file.name))
  input.value = ''
  pendingFiles.value = files
  uploadError.value = ''
}

const uploadAllImages = async () => {
  uploading.value = true
  uploadError.value = ''
  uploadedCount.value = 0
  try {
    for (let start = 0; start < pendingFiles.value.length; start += BATCH_SIZE) {
      const batch = pendingFiles.value.slice(start, start + BATCH_SIZE)
      const uploaded = await uploadApi.uploadQuestionImages(batch)
      for (const item of uploaded) {
        imageMap.value[item.filename] = item.url
      }
      uploadedCount.value += batch.length
    }
    ElMessage.success(`已上传 ${uploadedCount.value} 张图片`)
  } catch (error: any) {
    uploadError.value = error.response?.data?.message || '图片上传失败，请检查文件大小与格式'
  } finally {
    uploading.value = false
  }
}

const onPickJson = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  jsonError.value = ''
  jsonWarning.value = ''
  parsed.value = null
  if (!file) return
  try {
    const data = JSON.parse(await file.text())
    bankTitle.value = typeof data?.title === 'string' ? data.title : ''
    const cases = Array.isArray(data?.cases) ? data.cases : []
    const quiz = Array.isArray(data?.quiz) ? data.quiz : []
    if (cases.length === 0 && quiz.length === 0) {
      jsonError.value = 'JSON 中没有 cases 或 quiz 数据'
      return
    }
    parsed.value = { title: bankTitle.value, cases, quiz }
  } catch {
    jsonError.value = 'JSON 解析失败，请检查文件格式'
  }
}

const collectImageRefs = (bank: ParsedBank): string[] => {
  const refs: string[] = []
  for (const item of bank.cases) {
    for (const block of item.blocks ?? []) {
      if (block.type === 'image' && typeof block.imageRef === 'string') {
        refs.push(block.imageRef)
      }
    }
  }
  return refs
}

const totalImageRefCount = computed(() => (parsed.value ? collectImageRefs(parsed.value).length : 0))
const matchedRefCount = computed(() => {
  if (!parsed.value) return 0
  return collectImageRefs(parsed.value).filter(ref => imageMap.value[ref]).length
})

const submitImport = async () => {
  if (!parsed.value) return
  if (!meta.domain.trim() || !meta.subdomain.trim() || !meta.subDomainCode.trim()) {
    jsonError.value = '请填写题库 domain / subdomain / subDomainCode'
    return
  }
  const missingRefs = collectImageRefs(parsed.value).filter(ref => !imageMap.value[ref])
  if (missingRefs.length > 0) {
    jsonError.value = `还有 ${missingRefs.length} 个图片引用未上传：${missingRefs.slice(0, 5).join('、')}…`
    return
  }
  jsonError.value = ''
  importing.value = true
  try {
    const bank = await questionBanksApi.importQuestionBank({
      questionBankData: {
        domain: meta.domain.trim(),
        subdomain: meta.subdomain.trim(),
        subDomainCode: meta.subDomainCode.trim(),
        title: bankTitle.value || meta.subdomain.trim(),
        cases: parsed.value.cases,
        quiz: parsed.value.quiz,
        images: imageMap.value
      },
      overwrite: overwrite.value
    })
    ElMessage.success('导入成功')
    router.push(`/admin/question-banks/${bank.id}`)
  } catch (error: any) {
    jsonError.value = error.response?.data?.message || '导入失败，请检查 JSON 与图片映射'
  } finally {
    importing.value = false
  }
}
</script>

<style scoped>
.case-import {
  padding: 20px;
}

.page-header h4 {
  margin: 0;
}

.image-map-summary ul {
  max-height: 200px;
  overflow-y: auto;
}
</style>
